/* UndoToast.jsx — calm operational Undo affordance.
 *
 * Premium dark pill that fades in for ~5s after a destructive-
 * but-reversible operational action (milestone progression).
 * One toast at a time. Single calm action. Auto-dismisses with
 * a quiet countdown.
 *
 * Doctrine:
 *   - NOT a notification stack.
 *   - NOT a flashy success banner.
 *   - Almost invisible unless needed.
 *   - Mobile thumb-friendly, never overlaps the bottom nav.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Undo2 } from "lucide-react";

const TOAST_MS = 5000;
const TICK_MS  = 100;

export function UndoToast({ open, label, onUndo, onDismiss, testid = "undo-toast" }) {
  const [remaining, setRemaining] = useState(TOAST_MS);
  const [leaving, setLeaving] = useState(false);
  const startedAt = useRef(null);
  const undoing = useRef(false);

  // Reset state every time the toast opens with a new payload.
  useEffect(() => {
    if (open) {
      undoing.current = false;
      setLeaving(false);
      setRemaining(TOAST_MS);
      startedAt.current = Date.now();
    }
  }, [open, label]);

  // Calm countdown. Tick every 100ms, fade-out at 0.
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      const left = TOAST_MS - (Date.now() - (startedAt.current || Date.now()));
      if (left <= 0) {
        clearInterval(id);
        setLeaving(true);
        // 220ms fade-out window, then notify parent.
        setTimeout(() => {
          if (!undoing.current) onDismiss?.();
        }, 220);
      } else {
        setRemaining(left);
      }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [open, onDismiss]);

  if (!open) return null;

  const seconds = Math.max(1, Math.ceil(remaining / 1000));

  const handleUndo = async () => {
    undoing.current = true;
    setLeaving(true);
    // brief fade-out window so the operator sees the toast
    // visually acknowledge the tap before it vanishes.
    setTimeout(() => onUndo?.(), 180);
  };

  // Portal to <body> so the toast escapes any animated/transformed
  // ancestor (e.g. .cf-page-enter) which would otherwise become
  // the containing block for `position: fixed`.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      data-testid={testid}
      data-leaving={leaving ? "true" : "false"}
      className={`
        fixed z-50
        right-4 sm:right-6
        bottom-[calc(env(safe-area-inset-bottom)+88px)] sm:bottom-6
        max-w-[min(92vw,360px)]
        cf-undo-toast
      `}
      style={{
        opacity: leaving ? 0 : 1,
        transform: leaving ? "translateY(8px)" : "translateY(0)",
        transition: "opacity 220ms ease-out, transform 220ms ease-out",
      }}
    >
      <div
        className="flex items-center gap-3 pl-4 pr-2 py-2 rounded-full"
        style={{
          background: "rgba(15,23,42,0.92)",
          backdropFilter: "saturate(140%) blur(14px)",
          WebkitBackdropFilter: "saturate(140%) blur(14px)",
          boxShadow: "0 12px 32px -16px rgba(15,23,42,0.45), 0 2px 6px rgba(15,23,42,0.20)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <span
          className="text-[12.5px] sm:text-[12px] text-white/95 font-medium leading-tight tracking-[-0.005em] truncate"
          data-testid={`${testid}-label`}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={handleUndo}
          className="cf-press inline-flex items-center gap-1.5 h-9 sm:h-8 px-3 sm:px-2.5 rounded-full text-[12px] sm:text-[11.5px] font-semibold text-white bg-white/10 hover:bg-white/20 transition-colors shrink-0"
          data-testid={`${testid}-undo-btn`}
          aria-label="Undo last operational action"
        >
          <Undo2 size={12} strokeWidth={2.25} />
          <span>Undo</span>
          <span className="text-white/55 tabular-nums">· {seconds}s</span>
        </button>
      </div>
    </div>,
    document.body
  );
}
